import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Eye, EyeOff, Plus, X, Edit, Trash2, Table, Map as MapIcon, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { PersonLocation } from '../../types';
import { stalkerAPI, getStaticFileUrl } from '../../services/api';
import { useSearch } from '../../contexts/SearchContext';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { STATUS_MAP } from '../../utils/enums';
import loggingService from '../../services/loggingService';

// Fix default Leaflet marker icons (broken in webpack/vite builds)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
	iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
	shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const createMatrixIcon = (isDark: boolean, avatarUrl?: string | null) => {
	if (avatarUrl) {
		return L.divIcon({
			className: '',
			html: `<div style="
				width:48px;height:48px;
				border:2px solid ${isDark ? '#10B981' : '#059669'};
				border-radius:50%;
				overflow:hidden;
				box-shadow:0 0 10px ${isDark ? 'rgba(16,185,129,0.8)' : 'rgba(5,150,105,0.6)'};
				background: ${isDark ? '#111' : '#fff'};
				display: flex;
				align-items: center;
				justify-content: center;
			">
				<img src="${getStaticFileUrl(avatarUrl)}" style="width:100%;height:100%;object-fit:cover;" />
			</div>`,
			iconSize: [48, 48],
			iconAnchor: [24, 24],
			popupAnchor: [0, -25],
		});
	}

	return L.divIcon({
		className: '',
		html: `<div style="
			width:14px;height:14px;
			background:${isDark ? '#10B981' : '#059669'};
			border:2px solid ${isDark ? '#065f46' : '#064e3b'};
			border-radius:50%;
			box-shadow:0 0 10px ${isDark ? 'rgba(16,185,129,0.8)' : 'rgba(5,150,105,0.6)'};
		"></div>`,
		iconSize: [14, 14],
		iconAnchor: [7, 7],
		popupAnchor: [0, -10],
	});
};

const GlobalMap = ({ locations, navigate, isDark }: { locations: PersonLocation[], navigate: any, isDark: boolean }) => {
	const [zoom, setZoom] = React.useState(6);
	const markers = locations.filter((loc: any) => loc.pl_lat && loc.pl_lng);

	const MapEvents = () => {
		useMapEvents({
			zoomend: (e) => {
				setZoom(e.target.getZoom());
			},
		});
		return null;
	};

	return (
		<MapContainer 
			center={[39.0, 35.0]} 
			zoom={6}
			attributionControl={false}
			style={{ height: '100%', width: '100%', backgroundColor: isDark ? '#0a0a0a' : '#f5f5f5' }}
		>
			<MapEvents />
			<TileLayer
				url={isDark 
					? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
					: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
				}
				attribution='&copy; <a href="https://carto.com/attributions">CARTO</a>'
			/>
			{markers.map((m: any, i) => (
				<Marker 
					key={i} 
					position={[parseFloat(m.pl_lat), parseFloat(m.pl_lng)]} 
					icon={createMatrixIcon(isDark, zoom >= 8 ? m.person_avatar : null)}
				>
					<Popup>
						<div style={{ fontFamily: 'monospace', fontSize: '10px', textTransform: 'uppercase', width: '220px', background: isDark ? '#0a0a0a' : '#fff', color: isDark ? '#d1d5db' : '#374151' }}>
							<div style={{ color: '#10b981', fontWeight: 'bold', marginBottom: '8px', borderBottom: `1px solid ${isDark ? '#333' : '#e5e7eb'}`, paddingBottom: '6px', fontSize: '11px' }}>
								{m.pl_locationTitle || '// UNTITLED_LOC'}
							</div>
							<div style={{ display: 'flex', gap: '12px', marginBottom: '10px' }}>
								{m.person_avatar ? (
									<img 
										src={getStaticFileUrl(m.person_avatar) || ''} 
										style={{ width: '60px', height: '60px', objectFit: 'cover', border: `2px solid ${isDark ? '#10B981' : '#059669'}` }} 
										alt="AVT"
									/>
								) : (
									<div style={{ width: '60px', height: '60px', background: isDark ? '#1a1a1a' : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${isDark ? '#333' : '#e5e7eb'}`, color: '#6b7280' }}>
										{(m.person_name || '?')[0]}{(m.person_surname || '?')[0]}
									</div>
								)}
								<div style={{ flex: 1 }}>
									<div style={{ color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '2px', lineHeight: 1.2 }}>{m.pl_location}</div>
									<div style={{ color: isDark ? '#e5e7eb' : '#111827', fontWeight: 'bold' }}>{m.person_name} {m.person_surname}</div>
								</div>
							</div>
							<button 
								onClick={() => navigate(`/stalker/persons/${m.pl_personId}`)} 
								style={{ color: '#10b981', border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontSize: '10px', textDecoration: 'underline', width: '100%', textAlign: 'left' }}
							>
								[ VIEW_ENTITY_PROFILE ]
							</button>
						</div>
					</Popup>
				</Marker>
			))}
		</MapContainer>
	);
};

const Locations: React.FC = () => {
	const { globalSearchTerm } = useSearch();
	const { isDark } = useDarkMode();
	const navigate = useNavigate();
	const [locations, setLocations] = useState<PersonLocation[]>([]);
	const [loading, setLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [showHidden, setShowHidden] = useState(false);
	const [editingLocation, setEditingLocation] = useState<PersonLocation | null>(null);
	const [viewType, setViewType] = useState<'table' | 'map'>('map');
	const [formData, setFormData] = useState({
		pl_personId: '',
		pl_locationTitle: '',
		pl_location: '',
		pl_status: 'active'
	});
	const [persons, setPersons] = useState<any[]>([]);
	const [sortField, setSortField] = useState<string | null>(null);
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
	const hasLoggedPageView = useRef(false);

	useEffect(() => {
		loadData();
		loadPersons();
		if (!hasLoggedPageView.current) {
			loggingService.logPageView('/stalker/locations', 'Stalker', 'Locations');
			hasLoggedPageView.current = true;
		}
	}, []);

	const loadData = async () => {
		try {
			const response = await stalkerAPI.locations.getAll();
			setLocations(response.data.data);
		} catch (error) {
			console.error('Error loading locations:', error);
		} finally {
			setLoading(false);
		}
	};

	const loadPersons = async () => {
		try {
			const response = await stalkerAPI.persons.getAll();
			setPersons(response.data.data);
		} catch (error) {
			console.error('Error loading persons:', error);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			if (editingLocation) {
				await stalkerAPI.locations.update(editingLocation.pl_id, formData);
				loggingService.logEdit('location', editingLocation.pl_id, formData.pl_location || 'Unknown', 'Stalker');
			} else {
				const response = await stalkerAPI.locations.create({
					...formData,
					pl_personId: Number(formData.pl_personId)
				});
				const locationId = response.data.data?.pl_id;
				if (locationId) {
					loggingService.logCreate('location', locationId, formData.pl_location || 'Unknown', 'Stalker');
				}
			}
			setShowModal(false);
			setEditingLocation(null);
			setFormData({ pl_personId: '', pl_locationTitle: '', pl_location: '', pl_status: 'active' });
			loadData();
		} catch (error) {
			console.error('Error saving location:', error);
		}
	};

	const handleDelete = async (id: number) => {
		if (!window.confirm('Are you sure you want to delete this location?')) return;
		try {
			const location = locations.find(l => l.pl_id === id);
			await stalkerAPI.locations.delete(id);
			if (location) {
				loggingService.logDelete('location', id, location.pl_location || 'Unknown', 'Stalker');
			}
			loadData();
		} catch (error) {
			console.error('Error deleting location:', error);
		}
	};

	const filteredLocations = locations.filter(location => {
		const matchesSearch = globalSearchTerm === '' ||
			location.pl_location?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			location.pl_locationTitle?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			location.person_name?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			location.person_surname?.toLowerCase().includes(globalSearchTerm.toLowerCase());
		const matchesHideFilter = showHidden || location.person_hideHome !== '1';
		return matchesSearch && matchesHideFilter;
	});

	const sortedLocations = [...filteredLocations].sort((a, b) => {
		if (!sortField) return 0;
		
		let aValue: any = a[sortField as keyof PersonLocation];
		let bValue: any = b[sortField as keyof PersonLocation];
		
		if (sortField === 'person_name') {
			aValue = a.person_name || '';
			bValue = b.person_name || '';
		} else if (sortField === 'person_surname') {
			aValue = a.person_surname || '';
			bValue = b.person_surname || '';
		}
		
		if (aValue === undefined || aValue === null) aValue = '';
		if (bValue === undefined || bValue === null) bValue = '';
		
		if (typeof aValue === 'string') {
			aValue = aValue.toLowerCase();
			bValue = bValue.toLowerCase();
		}
		
		if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
		if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
		return 0;
	});

	const handleSort = (field: string) => {
		if (sortField === field) {
			if (sortDirection === 'asc') {
				setSortDirection('desc');
			} else if (sortDirection === 'desc') {
				setSortField(null);
				setSortDirection('asc');
			}
		} else {
			setSortField(field);
			setSortDirection('asc');
		}
	};

	const SortIcon = ({ field }: { field: string }) => {
		if (sortField !== field) {
			return <ArrowUpDown className="w-4 h-4 ml-1" />;
		}
		return sortDirection === 'asc' ? <ArrowUp className="w-4 h-4 ml-1" /> : <ArrowDown className="w-4 h-4 ml-1" />;
	};

	const getStatusColor = (status: string) => {
		const s = (status || '').toLowerCase();
		if (s === 'active') return isDark ? 'text-emerald-400' : 'text-emerald-600';
		if (s === 'notactive' || s === 'not_active' || s === 'inactive') return isDark ? 'text-red-400' : 'text-red-600';
		return isDark ? 'text-neutral-400' : 'text-neutral-600';
	};

	if (loading) {
		return (
			<div className={`flex flex-col items-center justify-center h-64 font-mono text-xs ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>
				<div className="flex items-center space-x-2">
					<div className="w-1 h-3 bg-current animate-pulse" />
					<span>[ FETCHING_LOCATION_RECORDS... ]</span>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4 px-2 md:px-0 pb-20">
			{/* Leaflet CSS customization */}
			<style>{`
				.leaflet-container { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace !important; }
				.leaflet-popup-content-wrapper { 
					background: ${isDark ? '#0a0a0a' : '#ffffff'} !important; 
					color: ${isDark ? '#d1d5db' : '#374151'} !important; 
					border: 1px solid ${isDark ? '#333' : '#d1d5db'} !important; 
					border-radius: 0 !important; 
					box-shadow: 0 4px 16px rgba(0,0,0,0.5) !important;
				}
				.leaflet-popup-tip { 
					background: ${isDark ? '#0a0a0a' : '#ffffff'} !important;
					border-color: ${isDark ? '#333' : '#d1d5db'} !important;
				}
				.leaflet-popup-close-button { color: ${isDark ? '#6b7280' : '#9ca3af'} !important; }
			`}</style>

			{/* Header */}
			<div className={`flex flex-col md:flex-row items-start justify-between md:items-center p-4 border rounded-none ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'} gap-4`}>
				<div>
					<h1 className={`text-sm font-mono font-bold tracking-widest uppercase ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
						// SYS: KNOWN_LOCATIONS
					</h1>
					<p className={`text-[10px] font-mono mt-0.5 uppercase tracking-wider ${isDark ? 'text-emerald-500/70' : 'text-emerald-600/70'}`}>
						{globalSearchTerm ? `MATCHING_RECORDS: ${sortedLocations.length} / ${filteredLocations.length}` : `TOTAL_RECORDS: ${filteredLocations.length}`}
					</p>
				</div>
				<div className="flex items-center space-x-2 self-end md:self-auto flex-wrap gap-2">
					<div className={`flex items-center space-x-1 border p-1 ${isDark ? 'border-[#333] bg-[#0a0a0a]' : 'border-neutral-300 bg-neutral-50'}`}>
						<button
							onClick={() => setViewType('table')}
							className={`p-1.5 transition-colors ${viewType === 'table' ? (isDark ? 'bg-emerald-500/20 text-emerald-500' : 'bg-emerald-100 text-emerald-600') : (isDark ? 'text-neutral-500 hover:text-white' : 'text-neutral-500 hover:text-black')}`}
							title="DATA_TABLE_VIEW"
						>
							<Table className="w-3.5 h-3.5" />
						</button>
						<button
							onClick={() => setViewType('map')}
							className={`p-1.5 transition-colors ${viewType === 'map' ? (isDark ? 'bg-emerald-500/20 text-emerald-500' : 'bg-emerald-100 text-emerald-600') : (isDark ? 'text-neutral-500 hover:text-white' : 'text-neutral-500 hover:text-black')}`}
							title="GEOSPATIAL_VIEW"
						>
							<MapIcon className="w-3.5 h-3.5" />
						</button>
					</div>
					<button
						onClick={() => setShowHidden(!showHidden)}
						className={`flex items-center space-x-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider uppercase border transition-colors ${
							isDark 
								? showHidden ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-[#0a0a0a] border-[#333] text-neutral-500 hover:text-white' 
								: showHidden ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : 'bg-neutral-50 border-neutral-300 text-neutral-600 hover:text-black'
						}`}
						title={showHidden ? 'HIDE_SECURE_RECORDS' : 'SHOW_SECURE_RECORDS'}
					>
						{showHidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
						<span className="hidden sm:inline">{showHidden ? 'SECURE_VISIBLE' : 'SECURE_HIDDEN'}</span>
					</button>
					<button
						onClick={() => {
							setEditingLocation(null);
							setFormData({ pl_personId: '', pl_locationTitle: '', pl_location: '', pl_status: 'active' });
							setShowModal(true);
						}}
						className={`flex items-center space-x-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider uppercase border text-emerald-500 hover:bg-emerald-500/10 transition-colors ${isDark ? 'border-[#333] bg-[#0a0a0a]' : 'border-neutral-300 bg-neutral-50'}`}
					>
						<Plus className="w-3 h-3" />
						<span className="hidden sm:inline">ADD_RECORD</span>
					</button>
				</div>
			</div>

			{viewType === 'table' ? (
				<div className={`border p-4 ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
					<h2 className={`text-[10px] font-mono tracking-widest uppercase mb-4 pb-2 border-b ${isDark ? 'text-emerald-500 border-[#222]' : 'text-emerald-700 border-neutral-200'}`}>
						// DATA_TABLE: LOCATIONS
					</h2>
					<div className={`border overflow-hidden ${isDark ? 'border-[#222]' : 'border-neutral-200'}`}>
						<div className="overflow-x-auto">
							<table className="w-full text-[10px] font-mono whitespace-nowrap">
								<thead className={isDark ? 'bg-[#0a0a0a] border-b border-[#222]' : 'bg-neutral-50 border-b border-neutral-200'}>
									<tr>
										<th 
											className={`px-4 py-2 text-left font-normal uppercase cursor-pointer select-none transition-colors ${isDark ? 'text-neutral-500 hover:bg-[#1a1a1a]' : 'text-neutral-400 hover:bg-neutral-100'}`}
											onClick={() => handleSort('pl_locationTitle')}
										>
											<div className="flex items-center">
												IDENTIFIER_TITLE
												<SortIcon field="pl_locationTitle" />
											</div>
										</th>
										<th 
											className={`px-4 py-2 text-left font-normal uppercase cursor-pointer select-none transition-colors ${isDark ? 'text-neutral-500 hover:bg-[#1a1a1a]' : 'text-neutral-400 hover:bg-neutral-100'}`}
											onClick={() => handleSort('pl_location')}
										>
											<div className="flex items-center">
												GEOSPATIAL_COORDS
												<SortIcon field="pl_location" />
											</div>
										</th>
										<th 
											className={`px-4 py-2 text-left font-normal uppercase cursor-pointer select-none transition-colors ${isDark ? 'text-neutral-500 hover:bg-[#1a1a1a]' : 'text-neutral-400 hover:bg-neutral-100'}`}
											onClick={() => handleSort('person_name')}
										>
											<div className="flex items-center">
												LINKED_ENTITY
												<SortIcon field="person_name" />
											</div>
										</th>
										<th 
											className={`px-4 py-2.5 text-left font-normal uppercase cursor-pointer select-none transition-colors text-[10px] ${isDark ? 'text-neutral-500 hover:bg-[#1a1a1a]' : 'text-neutral-400 hover:bg-neutral-100'}`}
											onClick={() => handleSort('pl_status')}
										>
											<div className="flex items-center">
												STATUS_CODE
												<SortIcon field="pl_status" />
											</div>
										</th>
										<th className={`px-4 py-2.5 text-right font-normal uppercase text-[10px] ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
											ACTIONS
										</th>
									</tr>
								</thead>
								<tbody className={`divide-y ${isDark ? 'divide-[#222]' : 'divide-neutral-200'}`}>
									{sortedLocations.length > 0 ? (
										sortedLocations.map((location) => (
											<tr key={location.pl_id} className={`hover:bg-opacity-50 transition-colors ${isDark ? 'hover:bg-[#1a1a1a] bg-[#111]' : 'hover:bg-neutral-50 bg-white'}`}>
												<td className={`px-4 py-3 font-bold uppercase text-[12px] ${isDark ? 'text-emerald-500' : 'text-emerald-700'}`}>
													{location.pl_locationTitle || '-'}
												</td>
												<td className={`px-4 py-3 uppercase text-[11px] ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
													{location.pl_location}
												</td>
												<td className={`px-4 py-3`}>
													{location.person_name && location.person_surname ? (
														<div className="flex items-center space-x-3">
															{location.person_avatar ? (
																<img
																	src={getStaticFileUrl(location.person_avatar) || ''}
																	alt="AVT"
																	className={`w-8 h-8 object-cover border cursor-pointer hover:opacity-80 transition-all ${isDark ? 'border-[#333]' : 'border-neutral-300'}`}
																/>
															) : (
																<div className={`w-8 h-8 flex items-center justify-center font-mono text-[10px] border ${isDark ? 'bg-[#1a1a1a] text-neutral-500 border-[#333]' : 'bg-neutral-100 text-neutral-500 border-neutral-300'}`}>
																	{(location.person_name || '').charAt(0)}{(location.person_surname || '').charAt(0)}
																</div>
															)}
															<button
																onClick={() => navigate(`/stalker/persons/${location.pl_personId}`)}
																className={`uppercase font-bold tracking-wider hover:underline text-[12px] ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}
															>
																{location.person_name} {location.person_surname}
															</button>
														</div>
													) : (
														<span className={`uppercase tracking-wider text-[11px] ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>[ UNKNOWN_ENTITY ]</span>
													)}
												</td>
												<td className={`px-4 py-3 uppercase tracking-wider font-bold text-[11px] ${getStatusColor(location.pl_status)}`}>
													{STATUS_MAP[location.pl_status as keyof typeof STATUS_MAP] || location.pl_status}
												</td>
												<td className="px-4 py-2 text-right">
													<div className="flex items-center justify-end space-x-1">
														<button
															onClick={() => {
																setEditingLocation(location);
																setFormData({
																	pl_personId: String(location.pl_personId),
																	pl_locationTitle: location.pl_locationTitle || '',
																	pl_location: location.pl_location,
																	pl_status: location.pl_status
																});
																setShowModal(true);
															}}
															className={`p-1.5 border transition-colors ${isDark ? 'bg-black/80 border-[#333] hover:bg-[#222] text-neutral-400 hover:text-white' : 'bg-white/90 border-neutral-300 hover:bg-neutral-100 text-neutral-600 hover:text-black'}`}
															title="MODIFY"
														>
															<Edit className="w-3.5 h-3.5" />
														</button>
														<button
															onClick={() => handleDelete(location.pl_id)}
															className={`p-1.5 border transition-colors ${isDark ? 'bg-black/80 border-[#333] hover:bg-red-900/50 text-red-500 hover:text-red-400' : 'bg-white/90 border-neutral-300 hover:bg-red-50 text-red-500 hover:text-red-600'}`}
															title="PURGE"
														>
															<Trash2 className="w-3.5 h-3.5" />
														</button>
													</div>
												</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan={5} className={`px-4 py-8 text-center uppercase tracking-widest ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>
												[ NO_RECORDS_FOUND ]
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			) : (
				<div className={`border p-4 h-[75vh] min-h-[500px] flex flex-col ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
					<h2 className={`text-[10px] font-mono tracking-widest uppercase mb-4 pb-2 border-b flex justify-between ${isDark ? 'text-emerald-500 border-[#222]' : 'text-emerald-700 border-neutral-200'}`}>
						<span>// GLOBAL_THEATER: KNOWN_LOCATIONS</span>
						<span className={`text-[9px] ${isDark ? 'text-emerald-500/50' : 'text-emerald-700/50'}`}>TRACKING_ACTIVE</span>
					</h2>
					<div className={`flex-1 relative border ${isDark ? 'border-[#333]' : 'border-neutral-300'}`}>
						<GlobalMap locations={sortedLocations} navigate={navigate} isDark={isDark} />
					</div>
				</div>
			)}

			{/* Modal */}
			{showModal && createPortal(
				<div
					className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[1005] p-4"
					onClick={() => setShowModal(false)}
				>
					<div
						className={`border p-6 w-full max-w-md ${isDark ? 'bg-[#111] border-[#333]' : 'bg-white border-neutral-300'}`}
						onClick={(e) => e.stopPropagation()}
					>
						<div className={`flex justify-between items-center mb-6 pb-2 border-b ${isDark ? 'border-[#333]' : 'border-neutral-200'}`}>
							<h2 className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>
								{editingLocation ? '// EDIT_RECORD' : '// NEW_RECORD'}
							</h2>
							<button
								onClick={() => {
									setShowModal(false);
									setEditingLocation(null);
									setFormData({ pl_personId: '', pl_locationTitle: '', pl_location: '', pl_status: 'active' });
								}}
								className={`p-1 border transition-colors ${isDark ? 'border-[#333] hover:bg-[#222] text-neutral-400 hover:text-white' : 'border-neutral-200 hover:bg-neutral-100 text-neutral-500 hover:text-black'}`}
							>
								<X className="w-4 h-4" />
							</button>
						</div>
						<form onSubmit={handleSubmit} className="space-y-4 font-mono text-[10px] uppercase tracking-wider">
							<div>
								<label className={`block mb-1.5 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
									LINKED_ENTITY <span className="text-emerald-500">*</span>
								</label>
								<select
									value={formData.pl_personId}
									onChange={(e) => setFormData({ ...formData, pl_personId: e.target.value })}
									className={`w-full px-3 py-2 border outline-none transition-colors ${isDark ? 'bg-[#0a0a0a] border-[#333] focus:border-emerald-500/50 text-neutral-200' : 'bg-neutral-50 border-neutral-300 focus:border-emerald-500/50 text-neutral-800'}`}
									required
									disabled={!!editingLocation}
								>
									<option value="">[ SELECT_ENTITY ]</option>
									{persons.map((person) => (
										<option key={person.person_id} value={person.person_id}>
											{person.person_name} {person.person_surname}
										</option>
									))}
								</select>
							</div>
							<div>
								<label className={`block mb-1.5 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
									IDENTIFIER_TITLE
								</label>
								<input
									type="text"
									value={formData.pl_locationTitle}
									onChange={(e) => setFormData({ ...formData, pl_locationTitle: e.target.value })}
									className={`w-full px-3 py-2 border outline-none transition-colors placeholder:text-neutral-600 ${isDark ? 'bg-[#0a0a0a] border-[#333] focus:border-emerald-500/50 text-neutral-200' : 'bg-neutral-50 border-neutral-300 focus:border-emerald-500/50 text-neutral-800'}`}
									placeholder="e.g. SAFE_HOUSE_01"
								/>
							</div>
							<div>
								<label className={`block mb-1.5 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
									GEOSPATIAL_COORDS <span className="text-emerald-500">*</span>
								</label>
								<input
									type="text"
									value={formData.pl_location}
									onChange={(e) => setFormData({ ...formData, pl_location: e.target.value })}
									className={`w-full px-3 py-2 border outline-none transition-colors placeholder:text-neutral-600 ${isDark ? 'bg-[#0a0a0a] border-[#333] focus:border-emerald-500/50 text-neutral-200' : 'bg-neutral-50 border-neutral-300 focus:border-emerald-500/50 text-neutral-800'}`}
									placeholder="e.g. ISTANBUL, TK"
									required
								/>
							</div>
							<div>
								<label className={`block mb-1.5 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>STATUS_CODE</label>
								<select
									value={formData.pl_status}
									onChange={(e) => setFormData({ ...formData, pl_status: e.target.value })}
									className={`w-full px-3 py-2 border outline-none transition-colors ${isDark ? 'bg-[#0a0a0a] border-[#333] focus:border-emerald-500/50 text-neutral-200' : 'bg-neutral-50 border-neutral-300 focus:border-emerald-500/50 text-neutral-800'}`}
								>
									<option value="active">ACTIVE</option>
									<option value="notactive">NOT_ACTIVE</option>
								</select>
							</div>
							<div className="flex justify-end space-x-2 pt-4">
								<button
									type="button"
									onClick={() => {
										setShowModal(false);
										setEditingLocation(null);
										setFormData({ pl_personId: '', pl_locationTitle: '', pl_location: '', pl_status: 'active' });
									}}
									className={`px-4 py-2 border transition-colors ${isDark ? 'border-[#333] hover:bg-[#222] text-neutral-400 hover:text-white' : 'border-neutral-300 hover:bg-neutral-100 text-neutral-600 hover:text-black'}`}
								>
									CANCEL
								</button>
								<button
									type="submit"
									className={`px-4 py-2 border transition-colors ${isDark ? 'bg-[#0a0a0a] border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10' : 'bg-emerald-50 border-emerald-200 text-emerald-600 hover:bg-emerald-100'}`}
								>
									{editingLocation ? 'EXECUTE_UPDATE' : 'EXECUTE_CREATE'}
								</button>
							</div>
						</form>
					</div>
				</div>,
				document.body
			)}
		</div>
	);
};

export default Locations;
